import React, {
  memo, useCallback, useEffect, useMemo, useRef, useState,
} from 'react'

import { Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, object } from 'prop-types'

import FilterSearchInput from 'Pods/SearchPage/Components/Shared/FilterSearchInput/FilterSearchInput'
import { FILTER_NAME } from 'Pods/SearchPage/Shared/Tracking/constants'
import { useTrackFilterSelect } from 'Pods/SearchPage/Shared/Tracking/FilterTracking'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { addStyle, clearStyles, removeStyle } from 'Pods/SearchPage/Store/actions/styles'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import { getStyleGroupsForAssetType } from './styleGroups'

// Mobile-only collapse shows the first groups whole instead of clamping to a pixel height,
// so a group heading is never cut off from its own tags.
export const COLLAPSED_GROUP_COUNT = 2

function StyleContent({ searchInputRef = null, hideReset = false, collapsible = false }) {
  const t = useTranslate()

  const assetType = useSearchPageStore(state => state.assetType)
  const selectedStyles = useSearchPageStore(state => state.styles)
  const styleCounts = useSearchPageStore(state => state.filterCounts.style) || EMPTY_ARRAY
  const trackFilterSelect = useTrackFilterSelect()
  const [searchTerm, setSearchTerm] = useState('')
  const [showAll, setShowAll] = useState(false)
  // Initialized with current assetType so the effect skips on mount; only fires on real changes.
  // Only the local search term resets here - the styles themselves are cleared by setAssetType
  // (user-initiated changes only), so popstate restores keep their hydrated styles.
  const previousAssetTypeRef = useRef(assetType)

  useEffect(() => {
    if (previousAssetTypeRef.current === assetType) return
    previousAssetTypeRef.current = assetType
    setSearchTerm('')
  }, [assetType])

  const styleLabel = useCallback(style => t(`search_page.filters.style.styles.${style}`), [t])

  const availableGroups = useMemo(() => {
    const all = getStyleGroupsForAssetType(assetType)
    if (!searchTerm) return all
    const normalized = searchTerm.toLowerCase()
    return all
      .map(group => ({
        ...group,
        styles: group.styles.filter(style => styleLabel(style).toLowerCase().includes(normalized)),
      }))
      .filter(group => group.styles.length > 0)
  }, [assetType, searchTerm, styleLabel])

  const selectedSet = useMemo(() => new Set(selectedStyles), [selectedStyles])
  const countsByStyle = useMemo(
    () => new Map(styleCounts.map(({ key, count }) => [key, count])),
    [styleCounts],
  )
  const isStyleDisabled = style => !selectedSet.has(style) && countsByStyle.get(style) === 0
  const hasSelectedAnyFilters = selectedStyles.length > 0
  const hasOverflow = collapsible && availableGroups.length > COLLAPSED_GROUP_COUNT
  const isCollapsed = hasOverflow && !showAll
  const visibleGroups = isCollapsed ? availableGroups.slice(0, COLLAPSED_GROUP_COUNT) : availableGroups

  const handleToggle = (style) => {
    if (selectedSet.has(style)) {
      removeStyle(style)
    } else {
      trackFilterSelect(FILTER_NAME.STYLE, style)
      addStyle(style)
    }
  }

  const handleToggleShowAll = useCallback(() => setShowAll(value => !value), [])

  // The desktop dropdown caps and scrolls the groups (page scrolling is locked while it is open);
  // the collapsible drawer scrolls as a whole and relies on Show more instead.
  const groupsClassName = collapsible
    ? 'style-filter__groups'
    : 'style-filter__groups style-filter__groups--scrollable'

  return (
    <div className="style-filter">
      <FilterSearchInput
        id="style-filter-search"
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder={t('search_page.filters.style.find_placeholder')}
        inputRef={searchInputRef}
      />
      {visibleGroups.length > 0 ? (
        <div
          className={groupsClassName}
          role="group"
          aria-label={t('search_page.filters.style.tags_group_label')}
        >
          {visibleGroups.map(group => (
            <div
              key={group.key}
              className="style-filter__group"
              role="group"
              aria-labelledby={`style-filter-group-${group.key}`}
            >
              <div id={`style-filter-group-${group.key}`} className="style-filter__group-label cgt-overline-md">
                {t(`search_page.filters.style.groups.${group.key}`)}
              </div>
              <div className="style-filter__tags">
                {group.styles.map(style => (
                  <Button
                    key={style}
                    type="secondary"
                    size="tiny"
                    isActive={selectedSet.has(style)}
                    aria-pressed={selectedSet.has(style)}
                    disabled={isStyleDisabled(style)}
                    onClick={() => handleToggle(style)}
                    customClass="style-filter__tag"
                  >
                    {styleLabel(style)}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <span className="style-filter__empty cgt-body-base">
          {t('search_page.filters.style.no_matches')}
        </span>
      )}
      {hasOverflow && (
        <Button
          type="ghost-primary"
          size="small"
          customClass="filter-wrapper__show-more cgt-link-primary"
          onClick={handleToggleShowAll}
        >
          {t(`search_page.filters.style.${showAll ? 'show_less' : 'show_more'}`)}
        </Button>
      )}
      {!hideReset && (
        <Button
          type="ghost-primary"
          size="small"
          customClass="filter-wrapper__reset-button filter-wrapper__reset-button--inline"
          onClick={clearStyles}
          disabled={!hasSelectedAnyFilters}
        >
          {t('search_page.filters.style.reset')}
        </Button>
      )}
    </div>
  )
}

StyleContent.propTypes = {
  searchInputRef: object,
  hideReset: bool,
  collapsible: bool,
}

export default memo(StyleContent)
